import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { DateFilterValues } from '@components/Common/EntryFilters/DateFilterControlled';

// URL param keys
const PARAM_CUSTOMER = 'customer';
const PARAM_TERMINAL = 'terminal';
const PARAM_FROM = 'from';
const PARAM_TO = 'to';
const PARAM_GROUP_BY_REFERENCE = 'groupByRef';
const PARAM_GROUP_BY_LOADING = 'groupByLoad';
const PARAM_INCLUDE_INVOICING = 'invoicing';

export interface CustomerReportFilters {
  customerId: string | null;
  terminalId: string | null;
  startDate: string | null;
  endDate: string | null;
  groupByReference: boolean;
  groupByLoading: boolean;
  includeInvoicingInfo: boolean;
}

export interface UseCustomerReportFiltersResult {
  filters: CustomerReportFilters;
  dateFilter: DateFilterValues;
  setCustomerId: (customerId: string | null) => void;
  setTerminalId: (terminalId: string | null) => void;
  setDateFilter: (value: DateFilterValues) => void;
  setGroupByReference: (value: boolean) => void;
  setGroupByLoading: (value: boolean) => void;
  setIncludeInvoicingInfo: (value: boolean) => void;
  clearFilters: () => void;
}

/**
 * Hook to manage customer report filters via URL search params.
 *
 * URL format: /reports/customers?customer=xxx&terminal=xxx&from=xxx&to=xxx&groupByRef=true&groupByLoad=true&invoicing=true
 */
export function useCustomerReportFilters(): UseCustomerReportFiltersResult {
  const [searchParams, setSearchParams] = useSearchParams();

  // Read values from URL
  const filters = useMemo<CustomerReportFilters>(() => {
    const customerId = searchParams.get(PARAM_CUSTOMER);
    const terminalId = searchParams.get(PARAM_TERMINAL);
    const startDate = searchParams.get(PARAM_FROM);
    const endDate = searchParams.get(PARAM_TO);
    const groupByReference = searchParams.get(PARAM_GROUP_BY_REFERENCE) === 'true';
    const groupByLoading = searchParams.get(PARAM_GROUP_BY_LOADING) === 'true';
    const includeInvoicingInfo = searchParams.get(PARAM_INCLUDE_INVOICING) === 'true';

    return {
      customerId,
      terminalId,
      startDate,
      endDate,
      groupByReference,
      groupByLoading,
      includeInvoicingInfo,
    };
  }, [searchParams]);

  // Date filter for the controlled component
  const dateFilter = useMemo<DateFilterValues>(() => ({
    startDate: filters.startDate,
    endDate: filters.endDate,
  }), [filters.startDate, filters.endDate]);

  // Update customer in URL (also clears terminal when customer changes)
  const setCustomerId = useCallback((customerId: string | null) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);

      if (customerId) {
        newParams.set(PARAM_CUSTOMER, customerId);
      } else {
        newParams.delete(PARAM_CUSTOMER);
      }
      // Don't clear terminal here - let the component handle that logic

      return newParams;
    }, { replace: true });
  }, [setSearchParams]);

  // Update terminal in URL
  const setTerminalId = useCallback((terminalId: string | null) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);

      if (terminalId) {
        newParams.set(PARAM_TERMINAL, terminalId);
      } else {
        newParams.delete(PARAM_TERMINAL);
      }

      return newParams;
    }, { replace: true });
  }, [setSearchParams]);

  // Update date filter in URL
  const setDateFilter = useCallback((value: DateFilterValues) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);

      if (value.startDate) {
        newParams.set(PARAM_FROM, value.startDate);
      } else {
        newParams.delete(PARAM_FROM);
      }

      if (value.endDate) {
        newParams.set(PARAM_TO, value.endDate);
      } else {
        newParams.delete(PARAM_TO);
      }

      return newParams;
    }, { replace: true });
  }, [setSearchParams]);

  // Update boolean options
  const setGroupByReference = useCallback((value: boolean) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (value) {
        newParams.set(PARAM_GROUP_BY_REFERENCE, 'true');
      } else {
        newParams.delete(PARAM_GROUP_BY_REFERENCE);
      }
      return newParams;
    }, { replace: true });
  }, [setSearchParams]);

  const setGroupByLoading = useCallback((value: boolean) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (value) {
        newParams.set(PARAM_GROUP_BY_LOADING, 'true');
      } else {
        newParams.delete(PARAM_GROUP_BY_LOADING);
      }
      return newParams;
    }, { replace: true });
  }, [setSearchParams]);

  const setIncludeInvoicingInfo = useCallback((value: boolean) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (value) {
        newParams.set(PARAM_INCLUDE_INVOICING, 'true');
      } else {
        newParams.delete(PARAM_INCLUDE_INVOICING);
      }
      return newParams;
    }, { replace: true });
  }, [setSearchParams]);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  return {
    filters,
    dateFilter,
    setCustomerId,
    setTerminalId,
    setDateFilter,
    setGroupByReference,
    setGroupByLoading,
    setIncludeInvoicingInfo,
    clearFilters,
  };
}

export default useCustomerReportFilters;
